import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Briefcase, Check, Clock } from "lucide-react";
import ApplicationForm from "@/components/ApplicationForm";
import { useLanguage } from "@/context/LanguageContext";
import { sectorLabel, contractLabel, jobField, formatProvince, formatPublishedDate, PAGE_META } from "@/data/content";
import usePageMeta from "@/hooks/usePageMeta";
import { publicApi as api } from "@/lib/api";

export default function JobDetail() {
  const { t, lang } = useLanguage();
  const d = t.jobDetail;
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setError(true));
  }, [id]);

  // Dynamic meta: use job title/desc when available, otherwise a sensible fallback.
  const metaTitle = job
    ? `${jobField(job, "title", lang)} — ${sectorLabel(job.sector, lang)} | Yellow5`
    : PAGE_META[lang].jobDetailFallback.title;
  const metaDesc = job
    ? (jobField(job, "description", lang) || PAGE_META[lang].jobDetailFallback.description)
        .replace(/\s+/g, " ")
        .slice(0, 200)
    : PAGE_META[lang].jobDetailFallback.description;
  usePageMeta({ title: metaTitle, description: metaDesc, lang });

  if (error) return (
    <div className="pt-40 pb-28 text-center min-h-screen">
      <p className="text-y5-muted mb-6">{d.notFound}</p>
      <Link to="/offres" className="text-y5-yellow">{d.backLink}</Link>
    </div>
  );
  if (!job) return <div className="pt-40 text-center min-h-screen text-y5-muted">{d.loading}</div>;

  const title = jobField(job, "title", lang);
  const description = jobField(job, "description", lang);
  const requirements = jobField(job, "requirements", lang) || [];

  return (
    <div className="pt-32 pb-28 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
        <Link to="/offres" className="inline-flex items-center gap-2 text-y5-muted hover:text-y5-yellow mb-10 transition-colors">
          <ArrowLeft size={16} /> {d.back}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className="inline-block bg-y5-yellow/10 text-y5-yellow text-xs font-semibold uppercase tracking-wider px-3 py-1.5 mb-6">{sectorLabel(job.sector, lang)}</span>
            <h1 className="font-display text-3xl sm:text-4xl md:text-6xl tracking-tight uppercase leading-[0.95] mb-8">{title}</h1>
            <div className="flex flex-wrap gap-6 text-y5-muted mb-4">
              <span className="flex items-center gap-2"><MapPin size={16} className="text-y5-yellow" /> {job.location}{formatProvince(job.province, lang) ? `, ${formatProvince(job.province, lang)}` : ""}</span>
              <span className="flex items-center gap-2"><Briefcase size={16} className="text-y5-yellow" /> {contractLabel(job.contract_type, lang)}</span>
            </div>
            {formatPublishedDate(job.published_at || job.created_at, lang) && (
              <div className="flex items-center gap-2 text-y5-muted text-sm mb-10 pb-10 border-b border-y5-line">
                <Clock size={15} className="text-y5-yellow" /> {formatPublishedDate(job.published_at || job.created_at, lang)}
              </div>
            )}
            <h3 className="font-display text-xl uppercase tracking-wide mb-4">{d.description}</h3>
            <p className="text-white/80 leading-relaxed whitespace-pre-line mb-10">{description}</p>
            {requirements.length > 0 && (
              <>
                <h3 className="font-display text-xl uppercase tracking-wide mb-4">{d.requirements}</h3>
                <ul className="space-y-3">
                  {requirements.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80"><Check size={18} className="text-y5-yellow mt-0.5 shrink-0" /> {r}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-28 bg-y5-surface border border-y5-line p-8">
              <h3 className="font-display text-2xl mb-6">{d.apply}</h3>
              <ApplicationForm jobId={job.id} jobTitle={title} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
